import React, { useEffect, useRef, useState } from "react";
import ImageWithBasePath from "../../core/common/imageWithBasePath";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../router/all_routes";
import axios, { AxiosError } from 'axios';
import { API_BASE_URL } from "../../environment";

interface LoginResponse {
  success: boolean;
  token: string;
  usuario: {
    id: number;
    nome: string;
    email: string;
    id_conta: number;
    cargo: string;
    foto_perfil_url?: string;
  };
  permissoes?: {
    [module: string]: string[];
  };
}

interface ErrorResponse {
  success: boolean;
  message: string;
}

const TAB_ID = Math.random().toString(36).slice(2);

const Login: React.FC = () => {
  const route = all_routes;
  const navigate = useNavigate();
  const [isPasswordVisible, setPasswordVisible] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // URL base da API (removido /api/v1 para usar apenas o domínio base)
  const BASE_DOMAIN = API_BASE_URL.replace('/api/v1', '');

  // Função para construir URL completa da imagem
  const getFullImageUrl = (relativePath: string | null | undefined): string | null => {
    if (!relativePath) return null;
    if (relativePath.startsWith('http')) return relativePath;
    if (relativePath.startsWith('/')) {
      return `${BASE_DOMAIN}${relativePath}`;
    }
    return `${BASE_DOMAIN}/${relativePath}`;
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible((prevState) => !prevState);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
  
    try {
      const response = await axios.post<LoginResponse>(`${API_BASE_URL}/auth/login.php`, {
        email: email,
        senha: password
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true
      });
      
      if (response.data.success) {
        // Construir URL completa da imagem
        const fullImageUrl = getFullImageUrl(response.data.usuario.foto_perfil_url);
        
        // Salva o token e os dados do usuário COM A FOTO
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify({
          id: response.data.usuario.id,
          name: response.data.usuario.nome,
          email: response.data.usuario.email,
          cargo: response.data.usuario.cargo,
          account_id: response.data.usuario.id_conta,
          foto_perfil_url: fullImageUrl
        }));
        
        // Salvar permissões
        localStorage.setItem('permissions', JSON.stringify(response.data.permissoes || {}));
        
        console.log('Dados salvos no login:', {
          foto_perfil_url: fullImageUrl,
          usuario: response.data.usuario
        }); // DEBUG
        
        // Configura o token para futuras requisições
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
        
        // Avisa outras abas que o login ocorreu (fromTabId para a aba atual ignorar)
        if (typeof BroadcastChannel !== 'undefined') {
          const ch = new BroadcastChannel('a2r_idle_activity');
          ch.postMessage({ type: 'login', fromTabId: TAB_ID });
          ch.close();
        }

        // Navega para a página onde estava antes do logout (ou padrão)
        const redirectTo = sessionStorage.getItem('redirectAfterLogin') || route.dealsKanban;
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(redirectTo);
      } else {
        setError('Erro ao fazer login.');
      }
    } catch (err) {
      const error = err as AxiosError<ErrorResponse>;
      if (error.response?.status === 401) {
        setError('Email ou senha incorretos.');
      } else {
        setError(error.response?.data?.message || 'Erro ao fazer login. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Função para fazer logout
  const handleLogout = () => {
    // Remove dados do localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    
    // Remove o token dos headers do axios
    delete axios.defaults.headers.common['Authorization'];
    
    // Redireciona para a página de login
    navigate(route.login);
  };

  useEffect(() => {
    localStorage.setItem("menuOpened", "Dashboard");
  }, []);

  // Escuta login de outra aba e navega automaticamente
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;

    const channel = new BroadcastChannel('a2r_idle_activity');

    const handleMessage = (e: MessageEvent) => {
      if (e.data?.type === 'login' && e.data?.fromTabId !== TAB_ID) {
        const token = localStorage.getItem('token');
        if (token) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const redirectTo = sessionStorage.getItem('redirectAfterLogin') || route.dealsKanban;
          sessionStorage.removeItem('redirectAfterLogin');
          navigate(redirectTo);
        }
      }
    };

    channel.addEventListener('message', handleMessage);
    return () => {
      channel.removeEventListener('message', handleMessage);
      channel.close();
    };
  }, [navigate, route.dealsKanban]);

  return (
    <div className="account-content">
      <div className="d-flex flex-wrap w-100 vh-100 overflow-hidden account-bg-01">
        <div className="d-flex align-items-center justify-content-center flex-wrap vh-100 overflow-auto p-4 w-50 bg-backdrop">
          <form className="flex-fill" onSubmit={handleSubmit}>
            <div className="mx-auto mw-450">
              <div className="text-center mb-4">
                <ImageWithBasePath
                  src="assets/img/logo.png"
                  className="img-fluid logo"
                  alt="Logotipo"
                />
              </div>

              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              <div className="mb-3">
                <label className="col-form-label">Endereço de Email</label>
                <div className="position-relative">
                  <span className="input-icon-addon">
                    <i className="ti ti-mail"></i>
                  </span>
                  <input
                    type="email"
                    className="form-control"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="mb-3">
                <label className="col-form-label">Senha</label>
                <div className="pass-group">
                  <input
                    type={isPasswordVisible ? "text" : "password"}
                    className="pass-input form-control"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <span
                    className={`ti toggle-password ${isPasswordVisible ? "ti-eye" : "ti-eye-off"}`}
                    onClick={togglePasswordVisibility}
                  ></span>
                </div>
              </div>

              <div className="mb-3">
                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={isLoading}
                >
                  {isLoading ? 'Entrando...' : 'Entrar'}
                </button>
              </div>

              <div className="text-center">
                <p className="fw-medium text-gray">Copyright © 2024 - Webbex Digital</p>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;